import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 0,
    padding: theme.spacing(5, 2),
    maxHeight: "85vh",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      maxWidth: "unset",
      maxHeight: "unset"
    }
  },
  paperWidthLg: {
    padding: theme.spacing(5, 4),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(3, 2)
    }
  },
  title: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 0, 2.5, 0),
    margin: theme.spacing(1, 3, 3, 3),
    // margin: theme.spacing(1, 3, 2, 3),
    borderBottom: `solid 1px ${theme.palette.secondary[300]}`,
    "& h6": {
      fontSize: "1.5rem",
      display: "flex",
      alignItems: "center"
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(5)
    }
  },
  paddingNone: {
    marginBottom: 0
  },
  titleEditable: {
    borderBottom: "none",
    margin: theme.spacing(1, 3, 1, 3),
    padding: "0px"
  },
  dialogContent: {
    flex: "unset",
    minHeight: ({ contentMinHeight }) => contentMinHeight,
    overflow: "auto",
    padding: "12px 24px",
    // overflow: "hidden",
    // paddingTop: 0,
    display: "flex",
    flexDirection: "column"
  },
  dialogActions: {
    padding: theme.spacing(2.5, 3, 1, 3),
    [theme.breakpoints.down("sm")]: {
      paddingBottom: theme.spacing(4),
      "& >div": {
        width: "100%"
      }
    }
  },
  scrollable: {
    overflowY: "auto"
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  fillHeight: {
    height: "85vh",
    display: "flex",
    flexDirection: "column"
  },
  contentFillHeight: {
    flex: "1"
  }
}));

const ModalContent = React.memo(
  ({
    dialogTitle,
    editableTitle,
    padding,
    onClose,
    showCloseIcon,
    showCloseIconWithoutTitle,
    scrollable,
    fillHeight,
    dialogContent,
    dialogActions,
    contentMinHeight
  }) => {
    const classes = useStyles({ contentMinHeight });
    return (
      <>
        {!!dialogTitle && (
          <DialogTitle
            component="span"
            id="modal-title"
            className={clsx(classes.title, {
              [classes.titleEditable]: editableTitle,
              [classes.paddingNone]: padding === "none"
            })}
          >
            {dialogTitle}
            {onClose && showCloseIcon ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
        )}
        {onClose && showCloseIconWithoutTitle ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        ) : null}
        <DialogContent
          className={clsx(classes.dialogContent, {
            [classes.scrollable]: scrollable,
            [classes.contentFillHeight]: fillHeight
          })}
        >
          {dialogContent}
        </DialogContent>
        {!!dialogActions && (
          <DialogActions classes={{ root: classes.dialogActions }}>
            {dialogActions}
          </DialogActions>
        )}
      </>
    );
  },
  // Prevents dialog content from rerendering so new dialog content
  // does not flash while modal is closing
  (prevProps, newProps) => prevProps.open && !newProps.open
);

export default function Modal(props) {
  const theme = useTheme();
  const classes = useStyles();
  const {
    dialogActions,
    dialogTitle,
    dialogContent,
    open,
    onClose,
    scrollable,
    showCloseIcon,
    showCloseIconWithoutTitle,
    editableTitle,
    padding,
    fillHeight,
    contentMinHeight,
    ...otherDialogProps
  } = props;
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Dialog
      fullScreen={fullScreen}
      aria-labelledby="modal"
      classes={{
        paper: clsx(classes.paper, {
          [classes.fillHeight]: fillHeight && !fullScreen
        }),
        paperWidthLg: classes.paperWidthLg
      }}
      open={open}
      onClose={onClose}
      className="prevent-grid-layout-drag" // Prevents a bug where text fields lose focus when the admin is in Edit Widget Mode
      {...otherDialogProps}
    >
      <ModalContent
        open={open}
        dialogTitle={dialogTitle}
        editableTitle={editableTitle}
        padding={padding}
        onClose={onClose}
        showCloseIcon={showCloseIcon}
        showCloseIconWithoutTitle={showCloseIconWithoutTitle}
        scrollable={scrollable}
        fillHeight={fillHeight}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        contentMinHeight={contentMinHeight}
      />
    </Dialog>
  );
}

Modal.propTypes = {
  dialogActions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  dialogTitle: PropTypes.node,
  dialogContent: PropTypes.node,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  scrollable: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  showCloseIconWithoutTitle: PropTypes.bool,
  editableTitle: PropTypes.bool,
  fullWidth: PropTypes.bool,
  padding: PropTypes.oneOf(["default", "none"])
};

Modal.defaultProps = {
  maxWidth: "sm",
  scrollable: false,
  showCloseIcon: false,
  showCloseIconWithoutTitle: false,
  editableTitle: false,
  fullWidth: false,
  padding: "default"
};
